import React from "react";
import { AspectRatio, Box } from "@chakra-ui/react";
import { ProductFeaturesHero } from "components/hero";
import { LottieWithStaticSafariImage } from "ui/lottie";
import { type ProductFeature } from "ui/product-features";
import { remCalc } from "theme/utils";
import heroImage from "../../../../../public/assets/platform/match-booster/hero.png";
import firstMobileImage from "../../../../../public/assets/platform/match-booster/mobile-hero-1.png";
import secondMobileImage from "../../../../../public/assets/platform/match-booster/mobile-hero-2.png";
import thirdMobileImage from "../../../../../public/assets/platform/match-booster/mobile-hero-3.png";
import animationData from "./animation.json";

const features: ProductFeature[] = [
  {
    id: "mb-hero-feature-1",
    title: `Simply toggle on Match Booster`,
    description: "Click one toggle in your Hightouch Audience Builder to enable match boosting to any ad platform.",
    mobileImage: {
      src: firstMobileImage,
      alt: "An illustration of three audience groups for Remarketing, Concert Attendees, and Newsletter Subscribers.",
    },
  },
  {
    id: "mb-hero-feature-2",
    title: `Hightouch enriches your audience in-flight`,
    description:
      "As audiences are synced to ad platforms, Hightouch appends additional identifiers to increase match rates.",
    mobileImage: {
      src: secondMobileImage,
      alt: "An illustration showing Match Booster enabled. User data has been enriched with identifiers to include a user's personal email, phone number, work email, mobile IDs, and address data.",
    },
  },
  {
    id: "mb-hero-feature-3",
    title: `Increase reach on every ad platform`,
    description:
      "Increase the reach of your audiences to reduce wasted spend with suppression and increase reach with targeting.",
    mobileImage: {
      src: thirdMobileImage,
      alt: "Illustration of increased match rates by 35%, 48%, and 24% on TikTok, Facebook, and LinkedIn respectively.",
    },
  },
];

function HeroAnimation() {
  const placeholder = (
    <AspectRatio
      ratio={1320 / 522}
      mb="45px" // Match the layout of the mb diagram with the overlapped toggle
      mx={{ base: 0, xl: "-32px" }}
      w="auto"
      borderRadius="bigger"
      overflow="hidden"
    >
      <Box bg="brand.backgroundSecondary.light" boxSize="100%" />
    </AspectRatio>
  );

  return (
    <Box mx={{ base: 0, xl: "-32px" }} w="auto">
      <LottieWithStaticSafariImage
        isPriority
        animationData={animationData}
        ratio={1320 / 567}
        viewportAmount={0.4}
        image={heroImage}
        alt="Data flowing from custom audiences, into ad platforms with enriched identifiers, resulting in increased reach."
        placeholder={placeholder}
      />
    </Box>
  );
}

type HeroProps = {
  eyebrow: string;
  title: string;
  description: string;
};

export function MatchBoosterHero({ eyebrow, title, description }: HeroProps) {
  return (
    <ProductFeaturesHero
      theme="dark"
      variant="violet"
      eyebrow={eyebrow}
      title={title}
      description={description}
      titleMaxW="900px"
      media={<HeroAnimation />}
      productFeatures={features}
      mobileFeatureTextMinH={{ base: remCalc(132), sm: remCalc(88), md: "auto" }}
      spotlightHeight="90%"
      featureGridProps={{
        gap: "small",
        mb: "bigger",
        mx: { base: 0, xl: "-32px" },
      }}
    />
  );
}
